import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import type { PostureDriftEntry } from "./use-posture.ts";
import {
  hostChangeKindLabel,
  hostConfidenceLabel,
  hostConfidenceStatus,
  hostAttestationLabel,
  hostAttestationStatus,
  driftHref,
} from "./vocab.ts";

/**
 * Host drift alerts (task 11.10; design 12.2 "host drift alerts"; 12.3 drill-down; 12.4 semantic
 * table + status by icon+text, not colour alone).
 *
 * Reads the drift page's `items` and renders one row per host configuration drift entry, newest
 * first. Each row shows WHAT changed (`change_kind`) and the host's confidence and attestation at
 * the time — both nullable per contract, shown honestly as "Unreported" when the server sent no
 * reading rather than a fabricated value. Confidence and attestation are conveyed by `StatusBadge`
 * (icon + text, never colour alone — design 12.4): a weaker attestation / lower confidence reads as
 * warn/error with its word, so the signal does not depend on hue.
 *
 * Every row drills to the drift event behind it (`category=host_drift` + the entry's own `event_id`
 * + source + change kind) — the contract gives each entry an `event_id`, so the drill targets that
 * exact event (design 12.3). The table is the shared semantic `DenseTable`.
 */

export interface HostDriftTableProps {
  readonly entries: readonly PostureDriftEntry[];
}

export function HostDriftTable(props: HostDriftTableProps): ReactNode {
  const navigate = useNavigate();
  const rows = [...props.entries].sort((a, b) => b.ts - a.ts);

  const columns: readonly Column<PostureDriftEntry>[] = [
    {
      key: "ts",
      header: "When",
      isRowHeader: true,
      cell: (d) => (
        <time className="pst-num" dateTime={isoAttr(d.ts)}>
          {formatDateTime(d.ts)}
        </time>
      ),
    },
    {
      key: "change",
      header: "Change",
      cell: (d) => hostChangeKindLabel(d.change_kind),
    },
    {
      key: "confidence",
      header: "Host confidence",
      cell: (d) => (
        <StatusBadge
          level={hostConfidenceStatus(d.host_confidence)}
          label={hostConfidenceLabel(d.host_confidence)}
          title={`Host confidence: ${hostConfidenceLabel(d.host_confidence).toLowerCase()}.`}
          size="sm"
        />
      ),
    },
    {
      key: "attestation",
      header: "Attestation",
      cell: (d) => (
        <StatusBadge
          level={hostAttestationStatus(d.host_attestation)}
          label={hostAttestationLabel(d.host_attestation)}
          title={`Host attestation: ${hostAttestationLabel(d.host_attestation).toLowerCase()}.`}
          size="sm"
        />
      ),
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      cell: (d) => (
        <button
          type="button"
          className="pst-link"
          onClick={() => navigate(driftHref(d))}
          aria-label={`Open the host drift event (${hostChangeKindLabel(
            d.change_kind,
          ).toLowerCase()})`}
        >
          Event
        </button>
      ),
    },
  ];

  return (
    <section className="pst-section" aria-label="Host drift alerts">
      <div className="pst-section__head">
        <h2 className="pst-section__title">Host drift alerts</h2>
        <p className="pst-section__note">
          Host configuration drift, newest first. Confidence and attestation are shown by icon and
          text; a row drills to its event.
        </p>
      </div>
      <DenseTable
        caption="Host configuration drift alerts with change kind, host confidence and attestation"
        captionHidden
        columns={columns}
        rows={rows}
        rowKey={(d) => d.id}
        stickyHeader
        emptyMessage="No host drift was reported in this window. Widen the time range, or check that host sources are reporting drift."
      />
    </section>
  );
}
